import React, { useEffect, useRef } from 'react'
import { XMarkIcon, ChatBubbleLeftRightIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import type { VizDirective } from '../../../Generated/VizDirective.ts'
import { formatProvenance, graphLabel, isCubeChartKind } from '../../../Flux/Slice/Chat/chatViz.js'
import { exportElementAsPng } from '../../Component/ConfigurableVisualization/ExportPng.js'
import { ChatOtuTable } from './ChatOtuTable.js'
import { ChatCubeChart } from './ChatCubeChart.js'

/**
 * Build the context-aware "Ask about this" starter question for a chart. Uses the shared {@link graphLabel}
 * (chart kind + blast/taxon params) so the seeded question names the SPECIFIC view — the on-screen viz state
 * itself already rides along as `vizContext` when the user sends, so this is just a human-readable prompt.
 */
export function askAboutStarter(directive: VizDirective): string {
  return `About the ${graphLabel(directive)} — what stands out?`
}

/**
 * At-a-glance badge of the taxonomy lens a chart was computed under — the RESOLVED `directive.provenance`
 * (blast source · SPIN algorithm / group mode · as-of time), so the user always sees which source produced
 * the chart in front of them. Renders nothing when no provenance is known. Uses the shared chat badge look
 * (`badge badge-ghost badge-sm`, same as the evidence-kind chip).
 */
export function ProvenanceChip({ directive }: { directive: VizDirective }) {
  const text = formatProvenance(directive.provenance)
  if (!text) return null
  return (
    <span
      className="badge badge-ghost badge-sm shrink-0 font-normal"
      title={`Computed under: ${text}`}
      aria-label={`Taxonomy source: ${text}`}
    >
      {text}
    </span>
  )
}

/**
 * One chart panel on the canvas beside the chat. Persistent (not tied to a message); highlighted + scrolled
 * into view when it is the focused graph (freshly opened, or pointed at via focusChart). Dispatches on `chartKind`:
 * OTU_TABLE → standalone table, KRONA → embed iframe, cube kinds → the workspace-driven ChatCubeChart.
 */
export function GraphPanel({
  directive,
  focused,
  onClose,
  onAsk,
  bare,
  exportRef
}: {
  directive: VizDirective
  focused?: boolean
  onClose?: () => void
  /** Seed the chat composer with a context-aware question about THIS chart (does not auto-send). */
  onAsk?: (text: string) => void
  /**
   * Drop the panel's own header AND outer border/padding/focus-ring so an enclosing card (W-2 WorkspacePanel)
   * owns the single header — the panel then contributes ONLY its content. The card hoists this panel's actions
   * (ask · export · close) into its own header via {@link exportRef} + its own dispatches. Keeps the `focused`
   * scroll-into-view behaviour. Default false → the standalone GraphCanvas look (own header + chrome) is unchanged.
   */
  bare?: boolean
  /**
   * In `bare` mode, the enclosing card populates this ref with the PNG-export handler so its header can host the
   * Export button (the panel no longer renders one). Ignored when not bare.
   */
  exportRef?: React.MutableRefObject<(() => void) | null>
}) {
  const ref = useRef<HTMLDivElement>(null)
  const captureRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (focused) ref.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [focused])

  const onExport = async () => {
    const node = captureRef.current
    if (!node) return
    try {
      await exportElementAsPng(node, `${graphLabel(directive) || directive.chartKind}.png`, { padding: 8 })
    } catch (err) {
      // Never crash the panel on an export failure (e.g. cross-origin iframe content) — just log.
      console.error('Failed to export chart as PNG', err)
    }
  }

  // Publish the export handler for the enclosing card header (bare mode hoists actions out of this panel).
  useEffect(() => {
    if (!exportRef) return undefined
    exportRef.current = () => {
      void onExport()
    }
    return () => {
      exportRef.current = null
    }
  })

  // Bare: content only, no header. The card owns the single header and hosts label/ask/export/close.
  if (bare) {
    return (
      <div ref={ref} className="w-full">
        <div ref={captureRef}>
          <VizContent directive={directive} />
        </div>
      </div>
    )
  }

  return (
    <div
      ref={ref}
      className={`w-full rounded-xl border bg-base-100 p-3 transition-shadow ${
        focused ? 'border-primary ring-2 ring-primary/40' : 'border-gray-200'
      }`}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <div className="truncate text-sm font-semibold">{graphLabel(directive)}</div>
          <ProvenanceChip directive={directive} />
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {onAsk && (
            <button
              className="btn btn-ghost btn-xs gap-1"
              onClick={() => onAsk(askAboutStarter(directive))}
              title="Ask the assistant about this chart"
              aria-label="Ask about this chart"
            >
              <ChatBubbleLeftRightIcon className="w-4" />
              Ask about this
            </button>
          )}
          <button
            className="btn btn-ghost btn-xs gap-1"
            onClick={onExport}
            title="Export this chart as PNG"
            aria-label="Export this chart as PNG"
          >
            <ArrowDownTrayIcon className="w-4" />
            Export PNG
          </button>
          {onClose && (
            <button className="btn btn-ghost btn-xs" onClick={onClose} aria-label="Close chart">
              <XMarkIcon className="w-4" />
            </button>
          )}
        </div>
      </div>
      <div ref={captureRef}>
        <VizContent directive={directive} />
      </div>
    </div>
  )
}

function VizContent({ directive }: { directive: VizDirective }) {
  switch (directive.chartKind) {
    case 'OTU_TABLE':
      return <ChatOtuTable directive={directive} />
    case 'KRONA': {
      // Reuse the /krona-embed route in an iframe (own `window` → isolates Krona's legacy global handlers).
      // Forward the directive's data params so the sunburst renders on the requested source/threshold, not
      // just the hardcoded NCBI/all-samples default (G1 — Krona parity). The embed reads these from the URL.
      const kronaParams = new URLSearchParams({ isProject: String(directive.scopeType === 'project') })
      if (directive.blastSource) kronaParams.set('blastSource', directive.blastSource)
      if (directive.scalingMode) kronaParams.set('scalingMode', directive.scalingMode)
      if (directive.countMode) kronaParams.set('countMode', directive.countMode)
      if (directive.gradeThreshold != null) kronaParams.set('gradeThreshold', String(directive.gradeThreshold))
      return (
        <iframe
          title={directive.title ?? 'Krona chart'}
          src={`/krona-embed/${directive.scopeId}?${kronaParams.toString()}`}
          className="h-[480px] w-full rounded-lg border-0"
        />
      )
    }
    default:
      if (isCubeChartKind(directive.chartKind)) {
        return <ChatCubeChart directive={directive} />
      }
      return <div className="text-xs text-gray-500">Unsupported chart: {directive.chartKind}</div>
  }
}
